
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function BackupSolutionsPage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    backupNeeds: [] as string[],
    contactMethod: '',
    message: '',
  });
  const [messageLength, setMessageLength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'message') {
      setMessageLength(value.length);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      backupNeeds: checked
        ? [...prev.backupNeeds, value]
        : prev.backupNeeds.filter((need) => need !== value),
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, contactMethod: e.target.value }));
  };

  const formUrl = isNL
    ? 'https://readdy.ai/api/form/d6dgjbiff40lgbk6dcr0'
    : 'https://readdy.ai/api/form/d6dgjbiff40lgbk6dcr0';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (messageLength > 500) return;
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formBody = new URLSearchParams();
      formBody.append('firstName', formData.firstName);
      formBody.append('lastName', formData.lastName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('employees', formData.employees);
      formBody.append('backupNeeds', formData.backupNeeds.join(', '));
      formBody.append('contactMethod', formData.contactMethod);
      formBody.append('message', formData.message);

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          employees: '',
          backupNeeds: [],
          contactMethod: '',
          message: '',
        });
        setMessageLength(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const backupNeedsOptions = [
    { value: 'Cloud Backup', label: isNL ? 'Cloudback-up' : 'Cloud Backup' },
    { value: 'On-Premise Backup', label: isNL ? 'On-Premise Back-up' : 'On-Premise Backup' },
    { value: 'Disaster Recovery', label: isNL ? 'Noodherstel' : 'Disaster Recovery' },
    { value: 'Data Migration', label: isNL ? 'Datamigratie' : 'Data Migration' },
  ];

  return (
    <div className="bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-2 text-sm">
            <a href={`${prefix}/`} className="text-gray-500 hover:text-primary">
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-500">Software</span>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">
              {isNL ? 'Back-upoplossingen' : 'Backup Solutions'}
            </span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative w-full bg-gradient-to-r from-gray-50 to-white">
        <div className="container mx-auto px-4 py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="w-full">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {isNL ? 'Zakelijke Back-upoplossingen' : 'Enterprise Backup Solutions'}
              </h1>
              <p className="text-lg text-gray-700 mb-8">
                {isNL
                  ? 'Bescherm uw kritieke bedrijfsgegevens met uitgebreide back-up- en noodhersteloplossingen. Van cloudback-up tot on-premise systemen, wij zorgen ervoor dat uw gegevens altijd veilig en herstelbaar zijn.'
                  : 'Protect your critical business data with comprehensive backup and disaster recovery solutions. From cloud backup to on-premise systems, we ensure your data is always secure and recoverable.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {isNL ? 'Demo Plannen' : 'Schedule Demo'}
                </button>
                <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-download-line"></i>
                  </div>
                  {isNL ? 'Brochure Downloaden' : 'Download Brochure'}
                </button>
              </div>
            </div>
            <div className="w-full h-80 rounded-lg overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/cd27db0d99fcf02d09cf7d6b888e6412.jpg"
                alt={isNL ? 'Zakelijke Back-upoplossingen' : 'Enterprise Backup Solutions'}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Backup Solutions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Uitgebreide Back-upoplossingen' : 'Comprehensive Backup Solutions'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Kies uit ons aanbod van zakelijke back-upoplossingen, ontworpen om aan uw specifieke bedrijfsvereisten en nalevingsbehoeften te voldoen.'
                : 'Choose from our range of enterprise-grade backup solutions designed to meet your specific business requirements and compliance needs.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Cloud Backup */}
            <div className="feature-card bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-cloud-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Cloudback-up' : 'Cloud Backup'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Veilige, schaalbare cloudgebaseerde back-upoplossingen met automatische synchronisatie en wereldwijde toegankelijkheid.'
                  : 'Secure, scalable cloud-based backup solutions with automatic synchronization and global accessibility.'}
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? '99,9% uptimegarantie' : '99.9% uptime guarantee'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'End-to-end versleuteling' : 'End-to-end encryption'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Wereldwijde datacenters' : 'Global data centers'}
                </li>
              </ul>
            </div>

            {/* On-Premise Backup */}
            <div className="feature-card bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-server-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'On-Premise Back-up' : 'On-Premise Backup'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Lokale back-upoplossingen voor maximale controle en naleving van strikte vereisten voor gegevensresidentie.'
                  : 'Local backup solutions for maximum control and compliance with strict data residency requirements.'}
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Volledige gegevenscontrole' : 'Full data control'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Snel herstel' : 'High-speed recovery'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Nalevingsgereed' : 'Compliance ready'}
                </li>
              </ul>
            </div>

            {/* Hybrid Solutions */}
            <div className="feature-card bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
              <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-shuffle-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Hybride Oplossingen' : 'Hybrid Solutions'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Het beste van twee werelden met hybride back-up die lokale snelheid en cloudschaalbaarheid combineert.'
                  : 'Best of both worlds with hybrid backup combining local speed and cloud scalability.'}
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Flexibele implementatie' : 'Flexible deployment'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Kostenoptimalisatie' : 'Cost optimization'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Geautomatiseerde tiering' : 'Automated tiering'}
                </li>
              </ul>
            </div>

            {/* Disaster Recovery */}
            <div className="feature-card bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-red-600">
                  <i className="ri-shield-check-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Noodherstel' : 'Disaster Recovery'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Volledige noodherstelplanning en -implementatie om bedrijfscontinuïteit te waarborgen.'
                  : 'Complete disaster recovery planning and implementation to ensure business continuity.'}
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  RTO &lt; 4 {isNL ? 'uur' : 'hours'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  RPO &lt; 15 {isNL ? 'minuten' : 'minutes'}
                </li>
                <li className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center text-green-500 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? '24/7 monitoring' : '24/7 monitoring'}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Backup Solutions */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Waarom Kiezen voor Onze Back-upoplossingen' : 'Why Choose Our Backup Solutions'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Onze zakelijke back-upoplossingen bieden uitgebreide gegevensbescherming met geavanceerde functies ontworpen voor moderne bedrijven.'
                : 'Our enterprise backup solutions provide comprehensive data protection with advanced features designed for modern businesses.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Automated Backups */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-time-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Geautomatiseerde Back-ups' : 'Automated Backups'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? "Stel het in en vergeet het met intelligente geautomatiseerde back-upschema's die zich aanpassen aan uw bedrijfsbehoeften."
                  : 'Set it and forget it with intelligent automated backup schedules that adapt to your business needs.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'Flexibele planningsopties' : 'Flexible scheduling options'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'Incrementele en differentiële back-ups' : 'Incremental and differential backups'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'Geautomatiseerde verificatie en testen' : 'Automated verification and testing'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Advanced Encryption */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-lock-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Geavanceerde Versleuteling' : 'Advanced Encryption'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Militaire versleuteling beschermt uw gegevens zowel tijdens overdracht als in rust met zero-knowledge architectuur.'
                  : 'Military-grade encryption protects your data both in transit and at rest with zero-knowledge architecture.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'AES-256 versleutelingsstandaard' : 'AES-256 encryption standard'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'End-to-end versleuteling' : 'End-to-end encryption'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'Privésleutelbeheer' : 'Private key management'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Instant Recovery */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-flashlight-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Direct Herstel' : 'Instant Recovery'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Ga snel weer aan de slag met direct bestandsherstel en bare-metal herstelmogelijkheden.'
                  : 'Get back to business quickly with instant file recovery and bare-metal restore capabilities.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'Bestandsherstel met één klik' : 'One-click file restoration'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'Gedetailleerde herstelopties' : 'Granular recovery options'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">
                    {isNL ? 'Virtuele machine herstel' : 'Virtual machine recovery'}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Kies Uw Back-upabonnement' : 'Choose Your Backup Plan'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Flexibele prijsopties afgestemd op uw bedrijfsomvang en back-upvereisten. Alle abonnementen inclusief 24/7 ondersteuning en 99,9% uptime SLA.'
                : 'Flexible pricing options to match your business size and backup requirements. All plans include 24/7 support and 99.9% uptime SLA.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Essential Plan */}
            <div className="pricing-card bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold mb-2">{isNL ? 'Essentieel' : 'Essential'}</h3>
                <p className="text-gray-600 mb-4">{isNL ? 'Perfect voor kleine bedrijven' : 'Perfect for small businesses'}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold">&euro;99</span>
                  <span className="text-gray-600">/{isNL ? 'maand' : 'month'}</span>
                </div>
                <div className="text-sm text-gray-500">{isNL ? 'Tot 1TB opslag' : 'Up to 1TB storage'}</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Dagelijkse geautomatiseerde back-ups' : 'Daily automated backups'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? '30 dagen bewaarbeleid' : '30-day retention policy'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'AES-256 versleuteling' : 'AES-256 encryption'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'E-mailondersteuning' : 'Email support'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Webgebaseerd beheer' : 'Web-based management'}</span>
                </li>
              </ul>
              <button className="w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                {isNL ? 'Abonnement Kiezen' : 'Select Plan'}
              </button>
            </div>

            {/* Professional Plan */}
            <div className="pricing-card bg-primary text-white rounded-lg shadow-lg p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                  {isNL ? 'Meest Populair' : 'Most Popular'}
                </span>
              </div>
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold mb-2">{isNL ? 'Professioneel' : 'Professional'}</h3>
                <p className="text-blue-100 mb-4">{isNL ? 'Ideaal voor groeiende bedrijven' : 'Ideal for growing businesses'}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold">&euro;299</span>
                  <span className="text-blue-100">/{isNL ? 'maand' : 'month'}</span>
                </div>
                <div className="text-sm text-blue-200">{isNL ? 'Tot 5TB opslag' : 'Up to 5TB storage'}</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-400 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-blue-50">{isNL ? 'Alles in Essentieel, plus:' : 'Everything in Essential, plus:'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-400 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-blue-50">{isNL ? 'Continue gegevensbescherming' : 'Continuous data protection'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-400 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-blue-50">{isNL ? '90 dagen bewaarbeleid' : '90-day retention policy'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-400 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-blue-50">{isNL ? 'Prioritaire telefonische ondersteuning' : 'Priority phone support'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-400 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-blue-50">{isNL ? 'Geavanceerde rapportage' : 'Advanced reporting'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-400 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-blue-50">{isNL ? 'API-toegang' : 'API access'}</span>
                </li>
              </ul>
              <button className="w-full bg-white text-primary px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                {isNL ? 'Abonnement Kiezen' : 'Select Plan'}
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="pricing-card bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold mb-2">Enterprise</h3>
                <p className="text-gray-600 mb-4">{isNL ? 'Voor grote organisaties' : 'For large organizations'}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold">&euro;799</span>
                  <span className="text-gray-600">/{isNL ? 'maand' : 'month'}</span>
                </div>
                <div className="text-sm text-gray-500">{isNL ? 'Onbeperkte opslag' : 'Unlimited storage'}</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Alles in Professioneel, plus:' : 'Everything in Professional, plus:'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Aangepast bewaarbeleid' : 'Custom retention policies'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Toegewezen accountmanager' : 'Dedicated account manager'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'SLA-garanties' : 'SLA guarantees'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'White-label opties' : 'White-label options'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Aangepaste integraties' : 'Custom integrations'}</span>
                </li>
              </ul>
              <button className="w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                {isNL ? 'Neem Contact Op' : 'Contact Sales'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Seamless Integrations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{isNL ? 'Naadloze Integraties' : 'Seamless Integrations'}</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Onze back-upoplossingen integreren direct met uw bestaande IT-infrastructuur en populaire bedrijfsapplicaties.'
                : 'Our backup solutions integrate with your existing IT infrastructure and popular business applications out of the box.'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {/* Integration items omitted for brevity – same as original */}
            {/* ... */}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{isNL ? 'Technische Specificaties' : 'Technical Specifications'}</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Gedetailleerde technische informatie over onze back-upoplossingen en systeemvereisten.'
                : 'Detailed technical information about our backup solutions and system requirements.'}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {/* Backup Methods Supported */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center cursor-pointer"
                  onClick={() => toggleAccordion('backup-methods')}
                >
                  <span className="font-semibold text-lg">
                    {isNL ? 'Ondersteunde Back-upmethoden' : 'Backup Methods Supported'}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                    <i
                      className={
                        'ri-arrow-' +
                        (openAccordion === 'backup-methods' ? 'up' : 'down') +
                        '-s-line'
                      }
                    ></i>
                  </div>
                </button>
                {openAccordion === 'backup-methods' && (
                  <div className="px-6 pb-4">
                    {/* Content omitted for brevity – same as original */}
                  </div>
                )}
              </div>

              {/* Security Protocols */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center cursor-pointer"
                  onClick={() => toggleAccordion('security-protocols')}
                >
                  <span className="font-semibold text-lg">
                    {isNL ? 'Beveiligingsprotocollen' : 'Security Protocols'}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                    <i
                      className={
                        'ri-arrow-' +
                        (openAccordion === 'security-protocols' ? 'up' : 'down') +
                        '-s-line'
                      }
                    ></i>
                  </div>
                </button>
                {openAccordion === 'security-protocols' && (
                  <div className="px-6 pb-4">
                    {/* Content omitted for brevity – same as original */}
                  </div>
                )}
              </div>

              {/* Recovery Options */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center cursor-pointer"
                  onClick={() => toggleAccordion('recovery-options')}
                >
                  <span className="font-semibold text-lg">
                    {isNL ? 'Herstelopties' : 'Recovery Options'}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                    <i
                      className={
                        'ri-arrow-' +
                        (openAccordion === 'recovery-options' ? 'up' : 'down') +
                        '-s-line'
                      }
                    ></i>
                  </div>
                </button>
                {openAccordion === 'recovery-options' && (
                  <div className="px-6 pb-4">
                    {/* Content omitted for brevity – same as original */}
                  </div>
                )}
              </div>

              {/* System Requirements */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center cursor-pointer"
                  onClick={() => toggleAccordion('system-requirements')}
                >
                  <span className="font-semibold text-lg">
                    {isNL ? 'Systeemvereisten' : 'System Requirements'}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                    <i
                      className={
                        'ri-arrow-' +
                        (openAccordion === 'system-requirements' ? 'up' : 'down') +
                        '-s-line'
                      }
                    ></i>
                  </div>
                </button>
                {openAccordion === 'system-requirements' && (
                  <div className="px-6 pb-4">
                    {/* Content omitted for brevity – same as original */}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request Backup Quote Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left Column - Info */}
              <div className="w-full md:w-5/12 bg-primary p-8 text-white">
                <h2 className="text-2xl font-bold mb-4">{isNL ? 'Back-upofferte Aanvragen' : 'Request Backup Quote'}</h2>
                <p className="mb-6">
                  {isNL
                    ? 'Ontvang een offerte op maat voor zakelijke back-up- en noodhersteloplossingen afgestemd op uw bedrijfsbehoeften.'
                    : 'Get a customized quote for enterprise backup and disaster recovery solutions tailored to your business needs.'}
                </p>
                <div className="space-y-4 mb-8">
                  {/* Info items omitted for brevity – same as original */}
                </div>
                <div className="border-t border-white/20 pt-6">
                  <p className="font-semibold mb-2">{isNL ? 'Neem contact op met ons back-upteam:' : 'Contact our backup team:'}</p>
                  <div className="flex items-center mb-2">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-phone-line"></i>
                    </div>
                    <span>+31 (0)616300011</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-mail-line"></i>
                    </div>
                    <span>info@thyamis.com</span>
                  </div>
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="w-full md:w-7/12 p-8">
                <form
                  id={isNL ? 'backup-quote-form-nl' : 'backup-quote-form'}
                  data-readdy-form
                  onSubmit={handleSubmit}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Form fields omitted for brevity – same as original */}
                  </div>

                  {/* Backup Needs */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Back-upbehoeften' : 'Backup Needs'}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {backupNeedsOptions.map((option) => (
                        <label key={option.value} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="backupNeeds"
                            value={option.value}
                            checked={formData.backupNeeds.includes(option.value)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Contact Method */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Gewenste Contactmethode' : 'Preferred Contact Method'}
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="Email"
                          checked={formData.contactMethod === 'Email'}
                          onChange={handleRadioChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">E-mail</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="Phone"
                          checked={formData.contactMethod === 'Phone'}
                          onChange={handleRadioChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">{isNL ? 'Telefoon' : 'Phone'}</span>
                      </label>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      {isNL ? 'Aanvullende Vereisten' : 'Additional Requirements'}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors text-sm"
                      placeholder={isNL ? 'Vertel ons over uw specifieke back-upvereisten (max 500 tekens)' : 'Tell us about your specific backup requirements (max 500 characters)'}
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      <span>{messageLength}</span>/500 {isNL ? 'tekens' : 'characters'}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || messageLength > 500}
                    className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium w-full hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting
                      ? isNL
                        ? 'Verzenden...'
                        : 'Submitting...'
                      : isNL
                      ? 'Offerte Aanvragen'
                      : 'Request Quote'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                      {isNL
                        ? 'Bedankt! Uw offerteaanvraag is succesvol verzonden. Ons team neemt binnenkort contact met u op.'
                        : 'Thank you! Your quote request has been submitted successfully. Our team will contact you soon.'}
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                      {isNL
                        ? 'Sorry, er is een fout opgetreden bij het verzenden van uw aanvraag. Probeer het opnieuw of neem rechtstreeks contact met ons op.'
                        : 'Sorry, there was an error submitting your request. Please try again or contact us directly.'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 relative bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://readdy.ai/api/search-image?query=Modern%20data%20center%20corridor%20with%20server%20racks%20and%20blue%20lighting%20showing%20enterprise%20technology%20infrastructure%20and%20data%20protection%20systems%20in%20professional%20business%20environment&width=1920&height=600&seq=backup-cta&orientation=landscape)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6 drop-shadow-lg">
              {isNL ? 'Bescherm Uw Bedrijfsgegevens Vandaag' : 'Protect Your Business Data Today'}
            </h2>
            <p className="text-white/90 mb-8 drop-shadow-lg">
              {isNL
                ? 'Wacht niet tot een ramp toeslaat. Implementeer zakelijke back-upoplossingen en waarborg uw bedrijfscontinuïteit met onze uitgebreide gegevensbeschermingsdiensten.'
                : "Don't wait for a disaster to strike. Implement enterprise-grade backup solutions and ensure your business continuity with our comprehensive data protection services."}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={`${prefix}/support`}
                className="bg-white text-primary px-6 py-3 rounded-lg whitespace-nowrap font-medium flex items-center hover:bg-gray-50 transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                {isNL ? 'Neem Contact Op met Ons Verkoopteam' : 'Contact Sales Team'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
